/**
 * Sidebar — the app-level left navigation used on the full-page views
 * (Dashboard, Datasets, Settings). Mirrors mockups/02-dashboard.html's <aside
 * class="sidebar">: logo, app name, nav links, and the signed-in user footer.
 *
 * The Editor uses its own full-bleed 3-column chrome and does NOT render this
 * sidebar, matching mockups/03-editor.html.
 *
 * Nav active state is derived from the current route via NavLink so the
 * highlighted item always reflects where the user is.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import { Map, Database, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Derive up to two uppercase initials from a display name or, failing that, the
// email local-part — for the avatar circle in the user footer.
function initialsFor(user) {
  const source = (user?.name || user?.email || '').trim();
  if (!source) return '?';
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function Sidebar() {
  const { user } = useAuth();

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img src="/logo-light.svg" alt="Front Porch Solutions" />
      </div>
      <div className="sidebar-app-name">Maps</div>

      <nav className="nav">
        <NavLink
          className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}
          to="/maps"
        >
          <Map /> Maps
        </NavLink>
        <NavLink
          className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}
          to="/datasets"
        >
          <Database /> Datasets
        </NavLink>
        <NavLink
          className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}
          to="/settings"
        >
          <Settings /> Settings
        </NavLink>
      </nav>

      <div className="sidebar-user">
        <div className="avatar avatar-sm">{initialsFor(user)}</div>
        <div className="sidebar-user-meta">
          <div className="sidebar-user-name">{user?.name || 'Signed in'}</div>
          <div className="sidebar-user-email">{user?.email}</div>
        </div>
      </div>
    </aside>
  );
}
